// FTSV-1332: all insights day boundaries use one fixed zone (UTC) so every
// viewer who picks the same date filter gets identical numbers.
//
// Convention: a Date's LOCAL calendar fields identify the intended day.
// The pickers emit browser-local-midnight Dates, and the preset builders below
// construct Dates whose local fields equal the current UTC calendar day.
// Serializers re-anchor those fields to UTC instants (epoch seconds), which
// also makes them forward-compatible with TZDate (whose getters return the
// configured zone's calendar fields).

export const INSIGHTS_TIMEZONE = 'UTC';

/** Start (00:00:00 UTC) of the calendar day identified by the date's calendar fields, in epoch seconds. */
export const toInsightsDayStartSeconds = (date: Date): number =>
  Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 1000,
  );

/** End (23:59:59 UTC) of the calendar day identified by the date's calendar fields, in epoch seconds. */
export const toInsightsDayEndSeconds = (date: Date): number =>
  Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59) /
      1000,
  );

// Presets must anchor "today" to the UTC clock, not the viewer's local day,
// otherwise the same instant yields different presets in different zones.
const insightsToday = (): Date => {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

/** Current UTC calendar day: [00:00:00, 23:59:59] as local-field Dates. */
export const getInsightsTodayRange = () => {
  const today = insightsToday();
  return {
    from: today,
    to: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    ),
  };
};

/** Last N UTC calendar days inclusive of today. Field arithmetic (not ms) keeps this DST-safe. */
export const getInsightsLastNDaysRange = (numDays: number) => {
  const today = insightsToday();
  return {
    from: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - (numDays - 1),
    ),
    to: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    ),
  };
};

/** UTC month to date. */
export const getInsightsCurrentMonthRange = () => {
  const today = insightsToday();
  return {
    from: new Date(today.getFullYear(), today.getMonth(), 1),
    to: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    ),
  };
};

/** Full previous UTC month. */
export const getInsightsPreviousMonthRange = () => {
  const today = insightsToday();
  return {
    from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
    to: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999),
  };
};
